import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ShoppingCart, User } from 'lucide-react';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Books = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [filter, setFilter] = useState('all');

  const collections = ['all', 'Histoire', 'Politique', 'Économie', 'Faits de société', 'Géopolitique'];

  useEffect(() => { fetchBooks(); }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/books`);
      setBooks(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBooks = filter === 'all' ? books : books.filter(b => b.collection === filter);

  return (
    <div className="min-h-screen bg-hermes-400 pt-20">
      <div className="fixed top-24 left-8 z-40">
        <img src="/logo-quid-novi.png" alt="Quid Novi" className="h-20 w-20" />
      </div>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">Nos livres</h1>
          <p className="text-xl text-white/90 max-w-3xl">Découvrez notre catalogue d'ouvrages consacrés aux sciences humaines et sociales.</p>
        </div>
      </section>

      {/* Filter */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2">
            {collections.map(c => (
              <button key={c} onClick={() => setFilter(c)} className={`px-4 py-2 text-sm font-medium transition-colors ${filter === c ? 'bg-white text-hermes-600' : 'bg-white/20 text-white hover:bg-white/30'}`}>
                {c === 'all' ? 'Tous' : c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12"><p className="text-white/80">Chargement...</p></div>
          ) : filteredBooks.length === 0 ? (
            <div className="text-center py-12"><p className="text-white/80">Aucun livre dans cette collection.</p></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {filteredBooks.map((book) => (
                <article key={book.id} className="group cursor-pointer" onClick={() => setSelectedBook(book)}>
                  <div className="aspect-[2/3] bg-white shadow-lg overflow-hidden mb-3 relative">
                    {book.cover_image_url ? (
                      <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-stone-100"><BookOpen className="w-12 h-12 text-stone-400" /></div>
                    )}
                  </div>
                  <h3 className="font-medium text-white line-clamp-2 mb-1">{book.title}</h3>
                  <p className="text-sm text-white/70">{book.author}</p>
                  {book.price && <p className="text-sm font-medium text-white mt-1">{book.price} €</p>}
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Book Modal */}
      {selectedBook && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto" onClick={() => setSelectedBook(null)}>
          <div className="min-h-screen py-8 px-4 flex items-start justify-center">
            <div className="max-w-4xl w-full bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="grid grid-cols-1 md:grid-cols-3">
                {/* Cover */}
                <div className="md:col-span-1 bg-stone-100 p-6">
                  <div className="aspect-[2/3] sticky top-6">
                    {selectedBook.cover_image_url ? (
                      <img src={selectedBook.cover_image_url} alt={selectedBook.title} className="w-full h-full object-cover shadow-lg" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-stone-200"><BookOpen className="w-16 h-16 text-stone-400" /></div>
                    )}
                  </div>
                </div>
                
                {/* Content */}
                <div className="md:col-span-2 p-8">
                  <button onClick={() => setSelectedBook(null)} className="absolute top-4 right-4 text-stone-400 hover:text-stone-600 text-3xl">&times;</button>
                  
                  <span className="inline-block px-3 py-1 bg-hermes-100 text-hermes-700 text-sm font-medium mb-4">{selectedBook.collection}</span>
                  <h2 className="text-3xl font-serif font-bold text-stone-900 mb-2">{selectedBook.title}</h2>
                  <p className="text-lg text-hermes-600 mb-6">{selectedBook.author}</p>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-stone-600 mb-6">
                    {selectedBook.pages && <span>{selectedBook.pages} pages</span>}
                    {selectedBook.isbn && <span>ISBN: {selectedBook.isbn}</span>}
                    {selectedBook.publication_date && <span>Publié le {new Date(selectedBook.publication_date).toLocaleDateString('fr-FR')}</span>}
                  </div>

                  {selectedBook.price && (
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-3xl font-bold text-hermes-600">{selectedBook.price} €</span>
                      {selectedBook.purchase_url && (
                        <a href={selectedBook.purchase_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-hermes-600 text-white font-medium hover:bg-hermes-700 transition-colors">
                          <ShoppingCart className="w-5 h-5" />Acheter sur VA Éditions
                        </a>
                      )}
                    </div>
                  )}

                  {selectedBook.summary && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-stone-900 mb-3">Résumé</h3>
                      <div className="prose prose-stone max-w-none" dangerouslySetInnerHTML={{ __html: selectedBook.summary }} />
                    </div>
                  )}

                  {selectedBook.author_bio && (
                    <div className="mb-6 p-4 bg-stone-50 rounded">
                      <h3 className="text-lg font-semibold text-stone-900 mb-3 flex items-center gap-2"><User className="w-5 h-5" />L'auteur</h3>
                      <div className="prose prose-stone prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: selectedBook.author_bio }} />
                    </div>
                  )}

                  {selectedBook.excerpt && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-stone-900 mb-3">Extrait</h3>
                      <div className="prose prose-stone prose-sm max-w-none p-4 border-l-4 border-hermes-600 bg-hermes-50 italic" dangerouslySetInnerHTML={{ __html: selectedBook.excerpt }} />
                    </div>
                  )}

                  <div className="pt-4 border-t border-stone-200">
                    <ShareButtons url={`https://quidnovi-editions.fr/livres/${selectedBook.id}`} title={`${selectedBook.title} - ${selectedBook.author}`} description={selectedBook.summary?.replace(/<[^>]+>/g, '').substring(0, 200)} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Books;
